import React, { useCallback, useMemo, useState } from 'react';
import { Star, Heart, Video, MessageSquare, FileText, PlayCircle, BookOpen, Headphones, Search, ArrowUpDown, Clock, Sparkles, User, Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
interface FavoritesTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
type TabType = 'astrologers' | 'services' | 'products' | 'courses';
type SortType = 'recent' | 'rating' | 'price';
interface FavAstrologer {
  id: string;
  name: string;
  spec: string;
  rating: number;
  reviews: number;
  price: string;
  priceNum: number;
  initials: string;
  color: string;
  online: boolean;
  nextSlot?: string;
  addedDaysAgo: number;
  active: boolean;
}
interface FavService {
  id: string;
  title: string;
  astrologer: string;
  price: string;
  priceNum: number;
  rating: number;
  format: 'video' | 'text';
  addedDaysAgo: number;
}
interface FavProduct {
  id: string;
  title: string;
  type: string;
  price: string;
  priceNum: number;
  icon: string;
  addedDaysAgo: number;
}
interface FavCourse {
  id: string;
  title: string;
  lessons: number;
  duration: string;
  price: string;
  priceNum: number;
  addedDaysAgo: number;
}
const initialData = {
  astrologers: [{
    id: 'a1',
    name: 'Елена Смирнова',
    spec: 'Западная астрология · Натальная карта',
    rating: 4.9,
    reviews: 124,
    price: 'от 4 000 ₽',
    priceNum: 4000,
    initials: 'ЕС',
    color: 'bg-indigo-100 text-indigo-700',
    online: true,
    nextSlot: 'Сегодня, 18:00',
    addedDaysAgo: 1,
    active: true
  }, {
    id: 'a2',
    name: 'Михаил Волков',
    spec: 'Ведическая астрология · Прогностика',
    rating: 5.0,
    reviews: 86,
    price: 'от 4 500 ₽',
    priceNum: 4500,
    initials: 'МВ',
    color: 'bg-emerald-100 text-emerald-700',
    online: false,
    nextSlot: 'Завтра, 10:00',
    addedDaysAgo: 3,
    active: true
  }, {
    id: 'a3',
    name: 'Анна Коваль',
    spec: 'Синастрия · Отношения',
    rating: 4.8,
    reviews: 210,
    price: 'от 3 500 ₽',
    priceNum: 3500,
    initials: 'АК',
    color: 'bg-rose-100 text-rose-700',
    online: true,
    nextSlot: 'Сегодня, 20:30',
    addedDaysAgo: 7,
    active: true
  }, {
    id: 'a4',
    name: 'Дмитрий Орлов',
    spec: 'Хорарная астрология',
    rating: 4.6,
    reviews: 42,
    price: 'от 3 000 ₽',
    priceNum: 3000,
    initials: 'ДО',
    color: 'bg-amber-100 text-amber-700',
    online: false,
    addedDaysAgo: 14,
    active: false
  }] as FavAstrologer[],
  services: [{
    id: 's1',
    title: 'Разбор натальной карты',
    astrologer: 'Елена Смирнова',
    price: '5 000 ₽',
    priceNum: 5000,
    rating: 4.9,
    format: 'video' as const,
    addedDaysAgo: 2
  }, {
    id: 's2',
    title: 'Соляр на год',
    astrologer: 'Михаил Волков',
    price: '4 000 ₽',
    priceNum: 4000,
    rating: 5.0,
    format: 'text' as const,
    addedDaysAgo: 5
  }] as FavService[],
  products: [{
    id: 'p1',
    title: 'Астрологический планер 2025',
    type: 'PDF',
    price: '1 500 ₽',
    priceNum: 1500,
    icon: 'book',
    addedDaysAgo: 1
  }, {
    id: 'p2',
    title: 'Набор медитаций для гармонизации планет',
    type: 'Аудио',
    price: '990 ₽',
    priceNum: 990,
    icon: 'audio',
    addedDaysAgo: 10
  }] as FavProduct[],
  courses: [{
    id: 'c1',
    title: 'Основы натальной астрологии',
    lessons: 12,
    duration: '4 недели',
    price: '15 000 ₽',
    priceNum: 15000,
    addedDaysAgo: 4
  }] as FavCourse[]
};
const PAGE_SIZE = 20;
export function FavoritesTab({
  theme,
  onNavigate
}: FavoritesTabProps) {
  const {
    showToast
  } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('astrologers');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortType, setSortType] = useState<SortType>('recent');
  const [favorites, setFavorites] = useState(initialData);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const totalCount = favorites.astrologers.length + favorites.services.length + favorites.products.length + favorites.courses.length;
  const tabs: {
    id: TabType;
    label: string;
    count: number;
  }[] = [{
    id: 'astrologers',
    label: 'Астрологи',
    count: favorites.astrologers.length
  }, {
    id: 'services',
    label: 'Услуги',
    count: favorites.services.length
  }, {
    id: 'products',
    label: 'Товары',
    count: favorites.products.length
  }, {
    id: 'courses',
    label: 'Курсы',
    count: favorites.courses.length
  }];
  const sortLabels: Record<SortType, string> = {
    recent: 'Недавние',
    rating: 'По рейтингу',
    price: 'По цене'
  };
  const nextSort = () => {
    const order: SortType[] = ['recent', 'rating', 'price'];
    setSortType(order[(order.indexOf(sortType) + 1) % order.length]);
  };
  const handleRemove = useCallback((type: TabType, id: string) => {
    setFavorites(prev => ({
      ...prev,
      [type]: (prev[type] as any[]).filter((item: any) => item.id !== id)
    }));
    setExpandedId(null);
    showToast('Удалено из избранного');
  }, [showToast]);
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setVisibleCount(PAGE_SIZE);
    setExpandedId(null);
  };
  const toggle = (id: string) => setExpandedId(expandedId === id ? null : id);
  const daysAgoLabel = (d: number) => {
    if (d === 0) return 'Сегодня';
    if (d === 1) return 'Вчера';
    if (d <= 6) return `${d} дн. назад`;
    return `${Math.floor(d / 7)} нед. назад`;
  };
  // Filtered lists
  const filteredAstrologers = useMemo(() => {
    let list = [...favorites.astrologers];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(a => a.name.toLowerCase().includes(q) || a.spec.toLowerCase().includes(q));
    }
    if (sortType === 'rating') list.sort((a, b) => b.rating - a.rating);else if (sortType === 'price') list.sort((a, b) => a.priceNum - b.priceNum);else list.sort((a, b) => a.addedDaysAgo - b.addedDaysAgo);
    return list;
  }, [favorites.astrologers, searchQuery, sortType]);
  const filteredServices = useMemo(() => {
    let list = [...favorites.services];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s => s.title.toLowerCase().includes(q) || s.astrologer.toLowerCase().includes(q));
    }
    if (sortType === 'rating') list.sort((a, b) => b.rating - a.rating);else if (sortType === 'price') list.sort((a, b) => a.priceNum - b.priceNum);else list.sort((a, b) => a.addedDaysAgo - b.addedDaysAgo);
    return list;
  }, [favorites.services, searchQuery, sortType]);
  const filteredProducts = useMemo(() => {
    let list = [...favorites.products];
    if (searchQuery.trim()) list = list.filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase()));
    if (sortType === 'price') list.sort((a, b) => a.priceNum - b.priceNum);else list.sort((a, b) => a.addedDaysAgo - b.addedDaysAgo);
    return list;
  }, [favorites.products, searchQuery, sortType]);
  const filteredCourses = useMemo(() => {
    let list = [...favorites.courses];
    if (searchQuery.trim()) list = list.filter(c => c.title.toLowerCase().includes(searchQuery.toLowerCase()));
    if (sortType === 'price') list.sort((a, b) => a.priceNum - b.priceNum);else list.sort((a, b) => a.addedDaysAgo - b.addedDaysAgo);
    return list;
  }, [favorites.courses, searchQuery, sortType]);
  const topRecommendation = favorites.astrologers.filter(a => a.online && a.active).sort((a, b) => b.rating - a.rating)[0];
  // Paginated current list
  const getCurrentList = (): any[] => {
    if (activeTab === 'astrologers') return filteredAstrologers;
    if (activeTab === 'services') return filteredServices;
    if (activeTab === 'products') return filteredProducts;
    return filteredCourses;
  };
  const currentList = getCurrentList();
  const visibleItems = currentList.slice(0, visibleCount);
  const hasMore = visibleCount < currentList.length;
  const renderEmpty = (msg: string, btn: string, page: string) => <div className="flex flex-col items-center py-12 text-center" data-id="element-18414">
      <Heart className="w-10 h-10 text-slate-200 mb-3" data-id="element-18415" />
      <h3 className="font-bold text-slate-900 text-sm mb-1" data-id="element-18416">{msg}</h3>
      <p className="text-xs text-slate-500 mb-4 max-w-xs" data-id="element-18417">
        Сохраняйте понравившееся, чтобы быстро возвращаться
      </p>
      <Button size="sm" onClick={() => onNavigate(page)} data-id="element-18418">
        {btn}
      </Button>
    </div>;
  return <div className="space-y-4 max-w-3xl pb-8" data-id="element-18419">
      <div data-id="element-18420">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-18421">
          Избранное
        </h2>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-18422">
          {totalCount} сохранённых элементов
        </p>
      </div>

      {topRecommendation && <div className="flex items-center gap-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-xl" data-id="element-18423">
          <Sparkles className="w-4 h-4 text-indigo-500 shrink-0" data-id="element-18424" />
          <span className="text-xs text-indigo-900 flex-1 min-w-0 truncate" data-id="element-18425">
            <span className="font-semibold" data-id="element-18426">{topRecommendation.name}</span>{' '}
            онлайн
            {topRecommendation.nextSlot && ` · ${topRecommendation.nextSlot}`}
          </span>
          <Button size="sm" className="text-xs shrink-0 h-7 px-3" onClick={() => onNavigate('astrologer-profile')} data-id="element-18427">
            Записаться
          </Button>
        </div>}

      <div className="flex gap-2" data-id="element-18428">
        <div className="relative flex-1" data-id="element-18429">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-18430" />
          <input type="text" placeholder="Поиск в избранном..." value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setVisibleCount(PAGE_SIZE);
        }} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 bg-white" data-id="element-18431" />
        </div>
        <button onClick={nextSort} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors shrink-0" data-id="element-18432">
          <ArrowUpDown className="w-3.5 h-3.5" data-id="element-18433" /> {sortLabels[sortType]}
        </button>
      </div>

      <div className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden pb-0.5" data-id="element-18434">
        {tabs.map(tab => <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${activeTab === tab.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-18435">
            {tab.label}{' '}
            <span className={`text-[10px] ${activeTab === tab.id ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-18436">
              {tab.count}
            </span>
          </button>)}
      </div>

      {/* List */}
      {currentList.length === 0 ? renderEmpty(activeTab === 'astrologers' ? 'Нет избранных астрологов' : activeTab === 'services' ? 'Нет избранных услуг' : activeTab === 'products' ? 'Нет избранных товаров' : 'Нет избранных курсов', activeTab === 'astrologers' ? 'Найти астролога' : 'В каталог', activeTab === 'astrologers' ? 'astrologers' : 'services-marketplace') : <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-18437">
          {visibleItems.map((item: any) => {
        const isExpanded = expandedId === item.id;
        if (activeTab === 'astrologers') {
          const a = item as FavAstrologer;
          return <div key={a.id} data-id="element-18438">
                  <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggle(a.id)} data-id="element-18439">
                    <div className="relative shrink-0" data-id="element-18440">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${a.color}`} data-id="element-18441">
                        {a.initials}
                      </div>
                      {a.online && <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" data-id="element-18442" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-18443">
                      <div className="flex items-center gap-1.5" data-id="element-18444">
                        <span className="font-medium text-sm text-slate-900 truncate" data-id="element-18445">
                          {a.name}
                        </span>
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" data-id="element-18446" />
                        <span className="text-[10px] font-bold text-slate-700 shrink-0" data-id="element-18447">
                          {a.rating}
                        </span>
                        {!a.active && <Badge variant="warning" size="sm" className="text-[8px] py-0 h-3.5 px-1" data-id="element-18448">
                            Неактивен
                          </Badge>}
                      </div>
                    </div>
                    <span className="text-xs font-medium text-indigo-600 shrink-0 hidden sm:block" data-id="element-18449">
                      {a.price}
                    </span>
                    <button onClick={e => {
                e.stopPropagation();
                handleRemove('astrologers', a.id);
              }} className="p-1 text-rose-400 hover:text-rose-600 shrink-0" data-id="element-18450">
                      <Heart className="w-3.5 h-3.5 fill-current" data-id="element-18451" />
                    </button>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-18452" />
                  </div>
                  {isExpanded && <div className="px-3 pb-3 -mt-0.5" data-id="element-18453">
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-18454">
                        <div className="text-[11px] text-slate-500" data-id="element-18455">
                          {a.spec}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500" data-id="element-18456">
                          <span data-id="element-18457">{a.price}</span>
                          <span data-id="element-18458">{a.reviews} отзывов</span>
                          {a.nextSlot && <span className="flex items-center gap-0.5" data-id="element-18459">
                              <Calendar className="w-3 h-3" data-id="element-18460" /> {a.nextSlot}
                            </span>}
                          <span className="flex items-center gap-0.5" data-id="element-18461">
                            <Clock className="w-3 h-3" data-id="element-18462" />{' '}
                            {daysAgoLabel(a.addedDaysAgo)}
                          </span>
                        </div>
                        <div className="flex gap-2 pt-1" data-id="element-18463">
                          <Button size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('astrologer-profile')} data-id="element-18464">
                            Записаться
                          </Button>
                          <Button variant="outline" size="sm" className="text-xs h-7 px-3" onClick={() => showToast('Открыт чат')} data-id="element-18465">
                            <MessageSquare className="w-3 h-3 mr-1" data-id="element-18466" /> Написать
                          </Button>
                          <Button variant="ghost" size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('astrologer-profile')} data-id="element-18467">
                            <User className="w-3 h-3 mr-1" data-id="element-18468" /> Профиль
                          </Button>
                        </div>
                      </div>
                    </div>}
                </div>;
        }
        if (activeTab === 'services') {
          const s = item as FavService;
          return <div key={s.id} data-id="element-18469">
                  <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggle(s.id)} data-id="element-18470">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${s.format === 'video' ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-18471">
                      {s.format === 'video' ? <Video className="w-3.5 h-3.5" data-id="element-18472" /> : <FileText className="w-3.5 h-3.5" data-id="element-18473" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-18474">
                      <span className="font-medium text-sm text-slate-900 truncate block" data-id="element-18475">
                        {s.title}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-slate-900 shrink-0" data-id="element-18476">
                      {s.price}
                    </span>
                    <button onClick={e => {
                e.stopPropagation();
                handleRemove('services', s.id);
              }} className="p-1 text-rose-400 hover:text-rose-600 shrink-0" data-id="element-18477">
                      <Heart className="w-3.5 h-3.5 fill-current" data-id="element-18478" />
                    </button>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-18479" />
                  </div>
                  {isExpanded && <div className="px-3 pb-3 -mt-0.5" data-id="element-18480">
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-18481">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500" data-id="element-18482">
                          <span data-id="element-18483">{s.astrologer}</span>
                          <span className="flex items-center gap-0.5" data-id="element-18484">
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-18485" />{' '}
                            {s.rating}
                          </span>
                          <span data-id="element-18486">
                            {s.format === 'video' ? 'Видео' : 'Текст'}
                          </span>
                          <span data-id="element-18487">{daysAgoLabel(s.addedDaysAgo)}</span>
                        </div>
                        <div className="flex gap-2 pt-1" data-id="element-18488">
                          <Button size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('service')} data-id="element-18489">
                            Заказать
                          </Button>
                          <Button variant="ghost" size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('service')} data-id="element-18490">
                            Подробнее
                          </Button>
                        </div>
                      </div>
                    </div>}
                </div>;
        }
        if (activeTab === 'products') {
          const p = item as FavProduct;
          return <div key={p.id} data-id="element-18491">
                  <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggle(p.id)} data-id="element-18492">
                    <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0" data-id="element-18493">
                      {p.icon === 'book' ? <BookOpen className="w-3.5 h-3.5" data-id="element-18494" /> : <Headphones className="w-3.5 h-3.5" data-id="element-18495" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-18496">
                      <span className="font-medium text-sm text-slate-900 truncate block" data-id="element-18497">
                        {p.title}
                      </span>
                    </div>
                    <Badge variant="neutral" size="sm" className="text-[8px] py-0 h-3.5 px-1 shrink-0 hidden sm:flex" data-id="element-18498">
                      {p.type}
                    </Badge>
                    <span className="text-xs font-bold text-slate-900 shrink-0" data-id="element-18499">
                      {p.price}
                    </span>
                    <button onClick={e => {
                e.stopPropagation();
                handleRemove('products', p.id);
              }} className="p-1 text-rose-400 hover:text-rose-600 shrink-0" data-id="element-18500">
                      <Heart className="w-3.5 h-3.5 fill-current" data-id="element-18501" />
                    </button>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-18502" />
                  </div>
                  {isExpanded && <div className="px-3 pb-3 -mt-0.5" data-id="element-18503">
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-18504">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500" data-id="element-18505">
                          <span data-id="element-18506">Тип: {p.type}</span>
                          <span data-id="element-18507">{daysAgoLabel(p.addedDaysAgo)}</span>
                        </div>
                        <div className="flex gap-2 pt-1" data-id="element-18508">
                          <Button size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('product-detail')} data-id="element-18509">
                            Купить
                          </Button>
                          <Button variant="ghost" size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('product-detail')} data-id="element-18510">
                            Подробнее
                          </Button>
                        </div>
                      </div>
                    </div>}
                </div>;
        }
        // courses
        const c = item as FavCourse;
        return <div key={c.id} data-id="element-18511">
                <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggle(c.id)} data-id="element-18512">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0" data-id="element-18513">
                    <PlayCircle className="w-3.5 h-3.5" data-id="element-18514" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-18515">
                    <span className="font-medium text-sm text-slate-900 truncate block" data-id="element-18516">
                      {c.title}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-slate-900 shrink-0" data-id="element-18517">
                    {c.price}
                  </span>
                  <button onClick={e => {
              e.stopPropagation();
              handleRemove('courses', c.id);
            }} className="p-1 text-rose-400 hover:text-rose-600 shrink-0" data-id="element-18518">
                    <Heart className="w-3.5 h-3.5 fill-current" data-id="element-18519" />
                  </button>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-18520" />
                </div>
                {isExpanded && <div className="px-3 pb-3 -mt-0.5" data-id="element-18521">
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-18522">
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500" data-id="element-18523">
                        <span data-id="element-18524">
                          {c.lessons} уроков · {c.duration}
                        </span>
                        <span data-id="element-18525">{daysAgoLabel(c.addedDaysAgo)}</span>
                      </div>
                      <div className="flex gap-2 pt-1" data-id="element-18526">
                        <Button size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('product-detail')} data-id="element-18527">
                          Подробнее
                        </Button>
                      </div>
                    </div>
                  </div>}
              </div>;
      })}
        </div>}

      {/* Load More */}
      {hasMore && <div className="text-center" data-id="element-18528">
          <Button variant="outline" size="sm" className="text-xs" onClick={() => setVisibleCount(v => v + PAGE_SIZE)} data-id="element-18529">
            Показать ещё ({currentList.length - visibleCount} осталось)
          </Button>
        </div>}

      {/* Count info */}
      {currentList.length > 0 && <div className="text-center text-[10px] text-slate-400" data-id="element-18530">
          Показано {Math.min(visibleCount, currentList.length)} из{' '}
          {currentList.length}
        </div>}
    </div>;
}